package com.castly.admin.ui.audiobooks

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.ui.FormField
import com.castly.admin.ui.components.ImportReportView
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.admin.ui.components.LabelSelector
import com.castly.shared.dto.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.files.File
import org.w3c.files.get

@Composable
fun OnixImportForm(
    apiClient: ApiClient,
    labels: List<LabelDto> = emptyList(),
    userLabelId: Int? = null,
    isLabelEditor: Boolean = false,
    onImportComplete: () -> Unit = {},
    onCancel: (() -> Unit)? = null
) {
    val colors = AppTheme.colors
    var selectedFile by remember { mutableStateOf<File?>(null) }
    var selectedLabelId by remember { mutableStateOf<Int?>(if (isLabelEditor) userLabelId else null) }
    var isParsing by remember { mutableStateOf(false) }
    var isConfirming by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var preview by remember { mutableStateOf<OnixImportPreview?>(null) }
    var importReport by remember { mutableStateOf<ImportReportDto?>(null) }
    var productActions by remember { mutableStateOf<Map<String, String>>(emptyMap()) }

    val scope = rememberCoroutineScope()

    Div({
        style {
            backgroundColor(Color(colors.surface))
            borderRadius(BorderRadius.lg)
            padding(Spacing.xxl)
            property("box-shadow", "0 2px 8px rgba(0,0,0,0.1)")
            themeTransition()
        }
    }) {
        H2({
            style {
                fontSize(Typography.xl)
                fontWeight("600")
                color(Color(colors.text))
                marginBottom(Spacing.lg)
            }
        }) {
            Text("Import from ONIX File")
        }

        P({
            style {
                fontSize(Typography.base)
                color(Color(colors.textMuted))
                marginBottom(Spacing.xxl)
            }
        }) {
            Text("Upload an ONIX 2.1 or 3.0 XML file. Only audiobook products (ProductForm AJ) will be imported.")
        }

        when {
            importReport != null -> {
                ImportReportView(importReport!!)
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.FlexEnd)
                        marginTop(Spacing.xxl)
                    }
                }) {
                    Button({
                        onClick {
                            importReport = null
                            preview = null
                            selectedFile = null
                            errorMessage = null
                            onCancel?.invoke()
                        }
                        style {
                            padding(Spacing.sm, Spacing.lg)
                            fontSize(Typography.base)
                            fontWeight("500")
                            color(Color("#ffffff"))
                            backgroundColor(Color(colors.primary))
                            border(0.px)
                            borderRadius(BorderRadius.md)
                            cursor("pointer")
                        }
                    }) { Text("Close") }
                }
            }

            preview != null -> {
                // Label selector
                if (labels.isNotEmpty()) {
                    Div({ style { marginBottom(Spacing.lg) } }) {
                        LabelSelector(
                            labels = labels,
                            selectedLabelId = selectedLabelId,
                            onLabelSelected = { selectedLabelId = it },
                            readOnly = isLabelEditor,
                            userLabelId = userLabelId
                        )
                    }
                }

                OnixPreviewView(
                    preview = preview!!,
                    productActions = productActions,
                    onActionChange = { isbn, action ->
                        productActions = productActions + (isbn to action)
                    },
                    isConfirming = isConfirming
                )

                // Error
                errorMessage?.let { error ->
                    ErrorBanner(error)
                }

                // Action buttons
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.FlexEnd)
                        gap(Spacing.md)
                        marginTop(Spacing.xxl)
                    }
                }) {
                    Button({
                        if (isConfirming) disabled()
                        onClick {
                            preview = null
                            productActions = emptyMap()
                            errorMessage = null
                        }
                        style {
                            padding(Spacing.sm, Spacing.lg)
                            fontSize(Typography.base)
                            fontWeight("500")
                            color(Color(colors.text))
                            backgroundColor(Color(colors.surface))
                            border(1.px, LineStyle.Solid, Color(colors.border))
                            borderRadius(BorderRadius.md)
                            cursor("pointer")
                        }
                    }) { Text("Back") }

                    Button({
                        val hasActions = productActions.values.any { it != "skip" }
                        if (!hasActions || isConfirming) disabled()
                        onClick {
                            isConfirming = true
                            errorMessage = null
                            scope.launch {
                                try {
                                    val actions = preview!!.products.map { product ->
                                        val diff = product.fieldDiff
                                        OnixProductAction(
                                            isbn = product.isbn,
                                            action = productActions[product.isbn] ?: "skip",
                                            fieldsToUpdate = if (productActions[product.isbn] == "update" && diff != null)
                                                diff.keys.toList()
                                            else emptyList()
                                        )
                                    }
                                    val report = apiClient.confirmOnixImport(
                                        OnixImportConfirmRequest(products = actions, labelId = selectedLabelId)
                                    )
                                    importReport = report
                                    onImportComplete()
                                } catch (e: Exception) {
                                    errorMessage = e.message ?: "Import failed"
                                } finally {
                                    isConfirming = false
                                }
                            }
                        }
                        style {
                            padding(Spacing.sm, Spacing.lg)
                            fontSize(Typography.base)
                            fontWeight("500")
                            color(Color("#ffffff"))
                            backgroundColor(Color(colors.primary))
                            border(0.px)
                            borderRadius(BorderRadius.md)
                            cursor("pointer")
                        }
                    }) { Text(if (isConfirming) "Importing..." else "Confirm Import") }
                }
            }

            else -> {
                // File selection
                FormField("ONIX XML File") {
                    Input(InputType.File) {
                        attr("accept", ".xml,.onix")
                        if (isParsing) disabled()
                        onChange { event ->
                            val files = event.target.files
                            if (files != null && files.length > 0) {
                                selectedFile = files[0]
                                errorMessage = null
                            }
                        }
                    }
                }

                selectedFile?.let { file ->
                    Div({
                        style {
                            backgroundColor(Color(colors.surfaceHover))
                            padding(Spacing.md)
                            borderRadius(BorderRadius.md)
                            marginTop(Spacing.md)
                            marginBottom(Spacing.lg)
                        }
                    }) {
                        Div({
                            style {
                                fontSize(Typography.base)
                                color(Color(colors.text))
                                fontWeight("500")
                            }
                        }) { Text("Selected: ${file.name}") }
                        Div({
                            style {
                                fontSize(13.px)
                                color(Color(colors.textMuted))
                                marginTop(Spacing.xs)
                            }
                        }) {
                            val sizeKb = (file.size.toDouble() / 1024.0).toInt()
                            Text("Size: ${sizeKb} KB")
                        }
                    }
                }

                if (isParsing) {
                    IndeterminateProgressBar("Parsing ONIX file...")
                }

                errorMessage?.let { error ->
                    ErrorBanner(error)
                }

                // Action buttons
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.FlexEnd)
                        gap(Spacing.md)
                        marginTop(Spacing.xxl)
                    }
                }) {
                    if (onCancel != null) {
                        Button({
                            if (isParsing) disabled()
                            onClick { if (!isParsing) onCancel() }
                            style {
                                padding(Spacing.sm, Spacing.lg)
                                fontSize(Typography.base)
                                fontWeight("500")
                                color(Color(colors.text))
                                backgroundColor(Color(colors.surface))
                                border(1.px, LineStyle.Solid, Color(colors.border))
                                borderRadius(BorderRadius.md)
                                cursor("pointer")
                            }
                        }) { Text("Cancel") }
                    }

                    Button({
                        if (selectedFile == null || isParsing) disabled()
                        onClick {
                            if (selectedFile != null && !isParsing) {
                                isParsing = true
                                errorMessage = null
                                scope.launch {
                                    try {
                                        val result = apiClient.importOnixPreview(selectedFile!!)
                                        preview = result
                                        // Default actions: import new, update duplicates
                                        val defaultActions = mutableMapOf<String, String>()
                                        result.products.forEach { product ->
                                            defaultActions[product.isbn] = when {
                                                product.validationStatus == "error" -> "skip"
                                                product.isDuplicate -> "update"
                                                else -> "import"
                                            }
                                        }
                                        productActions = defaultActions
                                    } catch (e: Exception) {
                                        errorMessage = e.message ?: "Failed to parse ONIX file"
                                    } finally {
                                        isParsing = false
                                    }
                                }
                            }
                        }
                        style {
                            padding(Spacing.sm, Spacing.lg)
                            fontSize(Typography.base)
                            fontWeight("500")
                            color(Color("#ffffff"))
                            backgroundColor(
                                if (selectedFile == null || isParsing) Color(colors.textMuted)
                                else Color(colors.primary)
                            )
                            border(0.px)
                            borderRadius(BorderRadius.md)
                            cursor(if (selectedFile == null || isParsing) "not-allowed" else "pointer")
                        }
                    }) { Text(if (isParsing) "Parsing..." else "Parse ONIX File") }
                }
            }
        }
    }

    Style {
        """
        @keyframes indeterminate {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
        }
        """.trimIndent()
    }
}

@Composable
private fun OnixPreviewView(
    preview: OnixImportPreview,
    productActions: Map<String, String>,
    onActionChange: (String, String) -> Unit,
    isConfirming: Boolean
) {
    val colors = AppTheme.colors

    // Summary
    Div({
        style {
            backgroundColor(Color(colors.surfaceHover))
            padding(Spacing.lg)
            borderRadius(BorderRadius.md)
            marginBottom(Spacing.lg)
        }
    }) {
        Div({
            style {
                fontSize(Typography.lg)
                fontWeight("600")
                color(Color(colors.text))
                marginBottom(Spacing.sm)
            }
        }) { Text("ONIX ${preview.onixVersion} Preview") }
        Div({
            style {
                fontSize(Typography.base)
                color(Color(colors.textMuted))
            }
        }) {
            Text("Total products: ${preview.totalProducts}")
            Br()
            Text("Audiobooks found: ${preview.audiobookProducts}")
            if (preview.skippedNonAudiobook > 0) {
                Br()
                Text("Non-audiobook skipped: ${preview.skippedNonAudiobook}")
            }
            if (preview.parseErrors.isNotEmpty()) {
                Br()
                Span({ style { color(Color(colors.danger)) } }) {
                    Text("Parse errors: ${preview.parseErrors.size}")
                }
            }
        }
    }

    // Product list
    if (preview.products.isNotEmpty()) {
        Div({
            style {
                fontSize(15.px)
                fontWeight("600")
                color(Color(colors.text))
                marginBottom(Spacing.md)
            }
        }) { Text("Products (${preview.products.size})") }

        Div({
            style {
                maxHeight(400.px)
                overflowY("auto")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(Spacing.sm)
            }
        }) {
            preview.products.forEach { product ->
                OnixProductRow(
                    product = product,
                    action = productActions[product.isbn] ?: "skip",
                    onActionChange = { action -> onActionChange(product.isbn, action) },
                    isConfirming = isConfirming
                )
            }
        }
    }

    // Parse errors
    if (preview.parseErrors.isNotEmpty()) {
        Div({
            style {
                marginTop(Spacing.lg)
                backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                padding(Spacing.md)
                borderRadius(BorderRadius.md)
            }
        }) {
            Div({
                style {
                    fontSize(Typography.base)
                    fontWeight("600")
                    color(Color(colors.danger))
                    marginBottom(Spacing.sm)
                }
            }) { Text("Parse Errors") }
            preview.parseErrors.forEach { err ->
                Div({
                    style {
                        fontSize(13.px)
                        color(Color(colors.danger))
                        marginBottom(Spacing.xs)
                    }
                }) {
                    Text("Product #${err.productIndex}: ${err.error}")
                }
            }
        }
    }
}

@Composable
private fun OnixProductRow(
    product: OnixProductPreview,
    action: String,
    onActionChange: (String) -> Unit,
    isConfirming: Boolean
) {
    val colors = AppTheme.colors

    val borderColor = when {
        product.validationStatus == "error" -> colors.danger
        product.isDuplicate -> colors.warning
        else -> colors.border
    }

    Div({
        style {
            backgroundColor(Color(colors.surface))
            border(1.px, LineStyle.Solid, Color(borderColor))
            borderRadius(BorderRadius.md)
            padding(Spacing.md)
        }
    }) {
        // Header row: title + action selector
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.FlexStart)
                gap(Spacing.md)
            }
        }) {
            // Title and metadata
            Div({ style { flex("1") } }) {
                Div({
                    style {
                        fontWeight("600")
                        fontSize(Typography.base)
                        color(Color(colors.text))
                    }
                }) { Text(product.title) }

                Div({
                    style {
                        fontSize(13.px)
                        color(Color(colors.textMuted))
                        marginTop(Spacing.xs)
                    }
                }) {
                    if (product.isbn.isNotBlank()) {
                        Span({ style { fontFamily("monospace") } }) { Text(product.isbn) }
                    }
                    product.authorName?.let { Span { Text(" | $it") } }
                    product.publisher?.let { Span { Text(" | $it") } }
                    product.language?.let { Span { Text(" | $it") } }
                }

                // Validation errors
                if (product.validationErrors.isNotEmpty()) {
                    Div({
                        style {
                            fontSize(12.px)
                            color(Color(colors.danger))
                            marginTop(Spacing.xs)
                        }
                    }) {
                        product.validationErrors.forEach { err ->
                            Span { Text("$err ") }
                        }
                    }
                }

                // Duplicate indicator
                if (product.isDuplicate) {
                    Div({
                        style {
                            fontSize(12.px)
                            color(Color(colors.warning))
                            marginTop(Spacing.xs)
                        }
                    }) { Text("Duplicate found in database") }
                }
            }

            // Action selector
            if (product.validationStatus != "error") {
                Select({
                    onChange { event ->
                        onActionChange(event.value ?: "skip")
                    }
                    if (isConfirming) disabled()
                    style {
                        padding(6.px, Spacing.sm)
                        fontSize(13.px)
                        borderRadius(BorderRadius.sm)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        backgroundColor(Color(colors.surface))
                        color(Color(colors.text))
                    }
                }) {
                    Option("skip", { if (action == "skip") selected() }) { Text("Skip") }
                    Option("import", { if (action == "import") selected() }) { Text("Import") }
                    if (product.isDuplicate) {
                        Option("update", { if (action == "update") selected() }) { Text("Update") }
                    }
                }
            } else {
                Span({
                    style {
                        fontSize(12.px)
                        color(Color(colors.danger))
                        fontWeight("500")
                    }
                }) { Text("Invalid") }
            }
        }

        // Field diff for duplicates
        val fieldDiff = product.fieldDiff
        if (product.isDuplicate && action == "update" && fieldDiff != null && fieldDiff.isNotEmpty()) {
            Div({
                style {
                    marginTop(Spacing.sm)
                    padding(Spacing.sm)
                    backgroundColor(Color(colors.surfaceHover))
                    borderRadius(BorderRadius.sm)
                    fontSize(12.px)
                }
            }) {
                Div({
                    style {
                        fontWeight("600")
                        color(Color(colors.text))
                        marginBottom(Spacing.xs)
                    }
                }) { Text("Fields that will change:") }
                fieldDiff.forEach { (field, diff) ->
                    Div({
                        style {
                            color(Color(colors.textMuted))
                            marginBottom(2.px)
                        }
                    }) {
                        Span({ style { fontWeight("500") } }) { Text("$field: ") }
                        Text("\"${diff.currentValue ?: ""}\" -> \"${diff.incomingValue ?: ""}\"")
                    }
                }
            }
        }
    }
}

@Composable
internal fun IndeterminateProgressBar(message: String) {
    val colors = AppTheme.colors
    Div({
        style { marginBottom(Spacing.lg) }
    }) {
        Div({
            style {
                fontSize(Typography.base)
                color(Color(colors.text))
                marginBottom(Spacing.sm)
                fontWeight("500")
            }
        }) { Text(message) }

        Div({
            style {
                width(100.percent)
                height(8.px)
                backgroundColor(Color(colors.border))
                borderRadius(BorderRadius.sm)
                overflow("hidden")
            }
        }) {
            Div({
                style {
                    height(100.percent)
                    width(100.percent)
                    backgroundColor(Color(colors.primary))
                    property("animation", "indeterminate 1.5s ease-in-out infinite")
                }
            })
        }
    }
}

@Composable
internal fun ErrorBanner(error: String) {
    val colors = AppTheme.colors
    Div({
        style {
            backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
            border(1.px, LineStyle.Solid, Color(colors.danger))
            borderRadius(BorderRadius.md)
            padding(Spacing.md)
            marginBottom(Spacing.lg)
        }
    }) {
        Div({
            style {
                fontSize(Typography.base)
                color(Color(if (colors == LightColors) "#991b1b" else "#fca5a5"))
                fontWeight("500")
            }
        }) { Text("Error") }
        Div({
            style {
                fontSize(13.px)
                color(Color(colors.danger))
                marginTop(Spacing.xs)
            }
        }) { Text(error) }
    }
}
